import { Menu } from '@mui/material';
import { type XYPosition } from '@xyflow/react';

import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { TileIcon } from '@/components/icons/TileIcon';
import { UserCircleIcon } from '@/components/icons/UserCircleIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { type DiagramSide } from '@/types/schema';

import { useDiagramEditing } from '../../contexts/diagramEditing.context';
import {
  DiagramEditorTool,
  type DiagramQuickAddTool,
} from './diagramEditorTools';

/** A line that the user dropped on the empty canvas. */
export interface DiagramQuickAddTarget {
  /** The drop point in the browser window, in screen pixels. */
  client: XYPosition;
  /** The drop point on the canvas, in canvas pixels. */
  position: XYPosition;
  source: { nodeID: string; side: DiagramSide };
}

const QUICK_ADD_OPTIONS: {
  tool: DiagramQuickAddTool;
  label: string;
  icon: typeof TileIcon;
}[] = [
  { tool: DiagramEditorTool.Tile, label: 'Tile', icon: TileIcon },
  { tool: DiagramEditorTool.Icon, label: 'Icon', icon: UserCircleIcon },
];

/**
 * A menu at the end of a line that the user dropped on the empty canvas. It
 * adds a tile or an icon at the drop point, connected to the line's source.
 * Escape or a click outside the menu closes it with no change.
 */
export function DiagramQuickAddMenu({
  target,
  onClose,
}: {
  target: DiagramQuickAddTarget;
  onClose: () => void;
}) {
  const { addConnectedNode } = useDiagramEditing();
  return (
    <Menu
      open
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={{ left: target.client.x, top: target.client.y }}
      slotProps={{ paper: { sx: { minWidth: 160 } } }}
    >
      <SelectItemGroupLabel label="Add connected" />
      {QUICK_ADD_OPTIONS.map(({ tool, label, icon: Icon }) => (
        <MenuItem
          key={tool}
          label={label}
          icon={<Icon size={16} />}
          onClick={() => {
            addConnectedNode(target.source, target.position, tool);
            onClose();
          }}
        />
      ))}
    </Menu>
  );
}
